import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Calendar, Video, Clock, User } from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const Appointments = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [appointments, setAppointments] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    doctor_id: 'dr_001',
    doctor_name: 'Dr. Rajesh Kumar',
    appointment_date: '',
    appointment_time: '',
    type: 'video'
  });

  useEffect(() => {
    loadAppointments();
  }, []);

  const loadAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`);
      setAppointments(response.data);
    } catch (error) {
      console.error('Error loading appointments:', error);
    }
  };

  const handleBook = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, formData);
      toast.success('Appointment booked successfully!');
      setShowDialog(false);
      loadAppointments();
      setFormData({
        doctor_id: 'dr_001',
        doctor_name: 'Dr. Rajesh Kumar',
        appointment_date: '',
        appointment_time: '',
        type: 'video'
      });
    } catch (error) {
      toast.error('Failed to book appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleJoinCall = (appointmentId) => {
    navigate(`/telemedicine/${appointmentId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 sm:py-8 pt-24 " data-testid="appointments-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2">{t('nav.appointments')}</h1>
            <p className="text-gray-600 text-lg">Manage your consultations</p>
          </div>
          <Button
            onClick={() => setShowDialog(true)}
            className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-xl transition-all"
            data-testid="book-appointment-btn"
          >
            <Calendar className="w-5 h-5 mr-2" />
            Book Appointment
          </Button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {appointments.map((apt) => (
            <Card key={apt.id} className="p-6 bg-white shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center">
                  <User className="w-6 h-6 text-teal-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-800">{apt.doctor_name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    apt.status === 'scheduled' ? 'bg-green-100 text-green-700' :
                    apt.status === 'completed' ? 'bg-gray-100 text-gray-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {apt.status}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">{apt.appointment_date}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span className="text-sm">{apt.appointment_time}</span>
                </div>
                {apt.type === 'video' && (
                  <div className="flex items-center gap-2 text-teal-600">
                    <Video className="w-4 h-4" />
                    <span className="text-sm font-medium">Video Consultation</span>
                  </div>
                )}
              </div>

              {apt.status === 'scheduled' && apt.type === 'video' && (
                <Button
                  onClick={() => handleJoinCall(apt.id)}
                  className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 text-white"
                  data-testid={`join-call-${apt.id}`}
                >
                  <Video className="w-4 h-4 mr-2" />
                  Join Video Call
                </Button>
              )}
            </Card>
          ))}

          {appointments.length === 0 && (
            <div className="col-span-full text-center py-16">
              <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No appointments yet</p>
              <Button
                onClick={() => setShowDialog(true)}
                variant="outline"
                className="mt-4"
              >
                Book your first appointment
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book New Appointment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Doctor</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.doctor_name}
                onChange={(e) => setFormData({ ...formData, doctor_name: e.target.value })}
              >
                <option>Dr. Rajesh Kumar</option>
                <option>Dr. Priya Sharma</option>
                <option>Dr. Amit Patel</option>
              </select>
            </div>
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.appointment_date}
                onChange={(e) => setFormData({ ...formData, appointment_date: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Time</Label>
              <Input
                type="time"
                value={formData.appointment_time}
                onChange={(e) => setFormData({ ...formData, appointment_time: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Type</Label>
              <select
                className="w-full px-3 py-2 border rounded-md mt-1"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="video">Video Consultation</option>
                <option value="in-person">In-Person</option>
              </select>
            </div>
            <Button
              onClick={handleBook}
              disabled={loading}
              className="w-full bg-gradient-to-r from-teal-600 to-cyan-600"
              data-testid="book-appointment-submit"
            >
              {loading ? 'Booking...' : 'Book Appointment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Appointments;
